import React, { useState, useEffect, useRef } from 'react';
import { navigate } from '../../utils/navigation';
import { IoCloseOutline, IoNotificationsOutline } from 'react-icons/io5';
import { useNotifications } from '../../hooks/useNotifications';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { UserNotification } from '../../context/ConvexNotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { handleNavigation } from '../../utils/navigationUtils';
import { useNotificationAnalytics } from '../../hooks/useNotificationAnalytics';

// Feature flags now use Statsig - these constants are kept for compatibility
const NOTIFICATION_FLAGS = {
  BELL_NOTIFICATIONS_ENABLED: 'bell_notifications_enabled'
};

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Phase 3: Updated tab type to match new filtering logic
type TabType = 'active' | 'history';

const NotificationItem: React.FC<{
  notification: UserNotification;
  onMarkAsRead: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  onNavigate: () => void;
  isHistoryItem?: boolean;
  analytics?: ReturnType<typeof useNotificationAnalytics>;
}> = ({ notification, onMarkAsRead, onDismiss, onNavigate, isHistoryItem = false, analytics }) => {
  const isUnread = !isHistoryItem && !notification.read_at; // Never show unread for history items
  const isDismissed = !!notification.dismissed_at;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const messageRef = React.useRef<HTMLParagraphElement>(null);

  // Check if text is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (messageRef.current) {
        const { scrollHeight, clientHeight } = messageRef.current;
        setIsTruncated(scrollHeight > clientHeight);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [notification.message]);

  const handleNotificationClick = async () => {
    // Track notification click
    if (analytics) {
      const notificationAge = notification.created_at ? 
        Date.now() - new Date(notification.created_at).getTime() : undefined;
        
      analytics.trackNotificationClicked({
        notification_id: notification.id,
        notification_type: notification.notification_type,
        delivery_method: notification.delivery_method as "push" | "email" | "bell" | "banner" | undefined,
        category: notification.category,
        template_key: notification.template?.key,
        priority: notification.priority,
        time_to_interaction_ms: notificationAge,
        notification_age_ms: notificationAge,
        interaction_context: 'panel'
      });
    }
    
    // For any item with truncated text, toggle expansion
    if (isTruncated) {
      setIsExpanded(!isExpanded);
      return;
    }
    
    // For active items, mark as read
    if (isUnread) {
      await onMarkAsRead(notification.id);
    }
  };

  const handleActionClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isUnread) {
      await onMarkAsRead(notification.id);
    }
    
    // Track action click
    if (analytics) {
      const notificationAge = notification.created_at ? 
        Date.now() - new Date(notification.created_at).getTime() : undefined;
        
      analytics.trackNotificationActionClicked({
        notification_id: notification.id,
        notification_type: notification.notification_type,
        delivery_method: notification.delivery_method as "push" | "email" | "bell" | "banner" | undefined,
        category: notification.category,
        template_key: notification.template?.key,
        priority: notification.priority,
        time_to_interaction_ms: notificationAge,
        notification_age_ms: notificationAge,
        interaction_context: 'panel',
        action_url: notification.action_url,
        action_text: notification.action_text,
        resulted_in_navigation: true,
        navigation_target: notification.action_url
      });
    }
    
    if (notification.action_url) {
      handleNavigation(notification.action_url, navigate);
      onNavigate();
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('NotificationPanel: Dismiss button clicked for notification:', notification.id);
    try {
      await onDismiss(notification.id);
      console.log('NotificationPanel: Dismiss function completed for notification:', notification.id);
    } catch (error) {
      console.error('NotificationPanel: Error in dismiss handler:', error);
    }
  };

  return (
    <div
      className={`
        p-4 border-b border-gray-200 dark:border-gray-700
        hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200
        ${isUnread ? 'bg-blue-50 dark:bg-blue-900/10 cursor-pointer' : ''}
        ${isTruncated ? 'cursor-pointer' : ''}
        ${isDismissed ? 'opacity-60' : ''}
      `}
      onClick={handleNotificationClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isUnread && (
              <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></div>
            )}
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {notification.title}
            </h3>
          </div>
          <p 
            ref={messageRef}
            className={`text-sm text-gray-600 dark:text-gray-400 mb-2 transition-all duration-200 ${
              isExpanded ? '' : 'line-clamp-2'
            }`}
          >
            {notification.message}
          </p>
          {isTruncated && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                // Mark as read when expanding (show more)
                if (!isExpanded && isUnread) {
                  await onMarkAsRead(notification.id);
                }
                
                // Track text expansion
                if (!isExpanded && analytics) {
                  analytics.trackTextExpanded({
                    expanded_notification_id: notification.id,
                    unread_count: 0 // Will be updated from parent component
                  });
                }
                
                setIsExpanded(!isExpanded);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-2"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </span>
            {notification.action_text && notification.action_url && (
              <button
                type="button"
                onClick={handleActionClick}
                className="text-xs text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 hover:underline cursor-pointer"
              >
                {notification.action_text}
              </button>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-3">
          {!isDismissed && (
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              aria-label="Dismiss"
            >
              <IoCloseOutline className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const {
    bellNotifications,
    bannerNotifications,
    historyNotifications: convexHistoryNotifications,
    historyLoading: convexHistoryLoading,
    markAsRead: markAsReadOriginal,
    dismissNotification: dismissNotificationOriginal,
    isLoading
  } = useNotifications();
  const bellFeatureFlag = useFeatureFlag(NOTIFICATION_FLAGS.BELL_NOTIFICATIONS_ENABLED);
  const analytics = useNotificationAnalytics();

  // Wrapper functions to convert Promise<boolean> to Promise<void>
  const markAsRead = async (id: string): Promise<void> => {
    await markAsReadOriginal(id);
  };

  const dismissNotification = async (id: string): Promise<void> => {
    await dismissNotificationOriginal(id);
  };
  
  // Debug: Add global access to test dismiss functionality
  React.useEffect(() => {
    if (typeof window !== 'undefined' && user?.id) {
      (window as unknown as {
        debugNotificationDismiss: (notificationId: string) => Promise<boolean>;
        debugCurrentUser: string;
        debugActiveNotifications: Array<unknown>;
      }).debugNotificationDismiss = async (notificationId: string) => {
        try {
          const result = await dismissNotificationOriginal(notificationId);
          return result;
        } catch (error) {
          console.error('Debug: Dismiss error:', error);
          return false;
        }
      };

      (window as unknown as {
        debugNotificationDismiss: (notificationId: string) => Promise<boolean>;
        debugCurrentUser: string;
        debugActiveNotifications: Array<unknown>;
      }).debugCurrentUser = user.id;
      (window as unknown as {
        debugNotificationDismiss: (notificationId: string) => Promise<boolean>;
        debugCurrentUser: string;
        debugActiveNotifications: Array<unknown>;
      }).debugActiveNotifications = [...bellNotifications, ...bannerNotifications];
      // Debug functions set up - available in browser console
    }
  }, [user, dismissNotificationOriginal, bellNotifications, bannerNotifications]);
  
  // Phase 3: Updated to use 'active' and 'history' tabs
  const [filter, setFilter] = useState<TabType>('active');
  const [filteredNotifications, setFilteredNotifications] = useState<UserNotification[]>([]);
  const [historyNotifications, setHistoryNotifications] = useState<UserNotification[]>([]);

  // Store values in refs to capture snapshot at panel open time
  const analyticsRef = useRef(analytics);
  const bellNotificationsRef = useRef(bellNotifications);
  const filterRef = useRef(filter);

  // Update refs when values change
  useEffect(() => {
    analyticsRef.current = analytics;
    bellNotificationsRef.current = bellNotifications;
    filterRef.current = filter;
  }, [analytics, bellNotifications, filter]);

  // Lock body scroll when panel is open and track analytics
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Track panel opened only on initial open
      // Add a small delay to avoid tracking during rapid state changes
      const timeoutId = setTimeout(() => {
        const currentNotifications = bellNotificationsRef.current;
        const unreadCount = currentNotifications.filter(n => !n.read_at).length;
        analyticsRef.current.trackPanelOpened({
          panel_state: filterRef.current,
          unread_count: unreadCount,
          total_count: currentNotifications.length
        });
      }, 500); // Increased delay to prevent rapid tracking

      return () => clearTimeout(timeoutId);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]); // Only depend on isOpen to prevent excessive tracking

  // Load notifications based on selected tab
  useEffect(() => {
    if (!user?.id) {
      setFilteredNotifications([]);
      setHistoryNotifications([]);
      return;
    }

    if (filter === 'active') {
      // For active tab, show only non-dismissed notifications
      const allActiveNotifications = [...bellNotifications, ...bannerNotifications]
        .filter(n => !n.dismissed_at) // Only show non-dismissed notifications
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setFilteredNotifications(allActiveNotifications);
    } else {
      // For history tab, use Convex history notifications (real-time)
      // Sort by dismissed_at descending (most recently dismissed first)
      const sortedHistory = [...convexHistoryNotifications]
        .sort((a, b) => new Date(b.dismissed_at || b.created_at).getTime() - new Date(a.dismissed_at || a.created_at).getTime());
      setHistoryNotifications(sortedHistory);
      setFilteredNotifications(sortedHistory);
    }
  }, [bellNotifications, bannerNotifications, convexHistoryNotifications, filter, user?.id]);

  // Don't render if bell notifications are disabled
  // useFeatureFlag returns { isEnabled: boolean, isLoading: boolean, error: Error | null }
  if (!bellFeatureFlag.isEnabled) {
    return null;
  }

  // Calculate counts for tabs
  const activeNotifications = [...bellNotifications, ...bannerNotifications];
  const unreadCount = activeNotifications.filter(n => !n.read_at && !n.dismissed_at).length;
  const historyCount = historyNotifications.length;

  return (
    <>
      {/* Transparent Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full sm:w-96 bg-white dark:bg-gray-900 shadow-xl z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Notifications
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            aria-label="Close notifications"
          >
            <IoCloseOutline className="w-5 h-5" />
          </button>
        </div>

        {/* Phase 3: Updated Filter Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => {
              if (filter !== 'active') {
                analytics.trackPanelTabSwitched({
                  from_tab: filter,
                  to_tab: 'active',
                  unread_count: unreadCount,
                  total_count: activeNotifications.length
                });
                setFilter('active');
              }
            }}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200
              ${filter === 'active'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
            title="Shows notifications with active alerts enabled. Critical notifications always appear here."
          >
            Active {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            type="button"
            onClick={() => {
              if (filter !== 'history') {
                analytics.trackPanelTabSwitched({
                  from_tab: filter,
                  to_tab: 'history',
                  unread_count: unreadCount,
                  total_count: historyCount
                });
                setFilter('history');
              }
            }}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200
              ${filter === 'history'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }
            `}
            title="Shows previously active notifications that have been read or dismissed."
          >
            History
          </button>
        </div>

        {/* Actions Bar */}
        {filter === 'active' && filteredNotifications.length > 0 && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex gap-4">
            <button
              type="button"
              onClick={async () => {
                // Dismiss all notifications in the current filtered view
                await Promise.all(filteredNotifications.map(n => dismissNotification(n.id)));
              }}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
              disabled={isLoading}
              title="Dismiss all notifications"
            >
              Dismiss all
            </button>
          </div>
        )}

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto">
          {(filter === 'active' && isLoading && filteredNotifications.length === 0) ||
           (filter === 'history' && convexHistoryLoading && historyNotifications.length === 0) ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (filter === 'active' ? filteredNotifications : historyNotifications).length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <IoNotificationsOutline className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium mb-2">
                {filter === 'active' ? 'No active notifications' : 'No notification history'}
              </p>
              <p className="text-sm text-center px-4">
                {filter === 'active' 
                  ? 'All caught up! Check back later for updates.'
                  : 'Previously active notifications will appear here after you read or dismiss them.'
                }
              </p>
            </div>
          ) : (
            <div>
              {(filter === 'active' ? filteredNotifications : historyNotifications).map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismissNotification}
                  onNavigate={onClose}
                  isHistoryItem={filter === 'history'}
                  analytics={analytics}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}; 